import { useNavigation } from '@refinedev/core';
import { List, useTable } from '@refinedev/antd';
import { Table, Button, Tag, Space, Popconfirm, message, Modal, Avatar } from 'antd';
import { PlusOutlined, CheckOutlined, CloseOutlined, HistoryOutlined, UserOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { apiClient } from '../../lib/api-client';

interface PromoCode {
  id: number;
  code: string;
  type: 'PERCENTAGE' | 'FIXED_AMOUNT';
  value: number;
  maxUsages: number | null;
  currentUsages: number;
  isActive: boolean;
  startsAt: string | null;
  expiresAt: string | null;
}

interface PromoCodeUsage {
  id: number;
  createdAt: string;
  discountAmount: number;
  user: {
    id: number;
    fullName: string;
    phone: string;
  };
}

interface UsageHistoryData {
  promoCode: PromoCode;
  usages: PromoCodeUsage[];
  total: number;
}

export const PromoCodeList = () => {
  const { create } = useNavigation();
  const [usageModalOpen, setUsageModalOpen] = useState(false);
  const [usageData, setUsageData] = useState<UsageHistoryData | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  const { tableProps, tableQueryResult } = useTable<PromoCode>({
    resource: 'admin/promo-codes',
    syncWithLocation: true,
  });

  const handleToggle = async (id: number) => {
    try {
      await apiClient.put(`/admin/promo-codes/${id}/toggle`);
      message.success('Holat o\'zgartirildi');
      tableQueryResult.refetch();
    } catch {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleViewUsage = async (promoCode: PromoCode) => {
    setLoadingUsage(true);
    setUsageModalOpen(true);
    try {
      const response = await apiClient.get(`/admin/promo-codes/${promoCode.id}/usage`);
      setUsageData(response.data);
    } catch {
      message.error('Ishlatilish tarixini yuklashda xatolik');
      setUsageModalOpen(false);
    } finally {
      setLoadingUsage(false);
    }
  };

  const formatValue = (record: PromoCode) => {
    if (record.type === 'PERCENTAGE') {
      return `${record.value}%`;
    }
    return new Intl.NumberFormat('uz-UZ').format(record.value) + ' so\'m';
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + ' so\'m';
  };

  const usageColumns = [
    {
      title: 'Foydalanuvchi',
      key: 'user',
      render: (_: unknown, record: PromoCodeUsage) => (
        <Space>
          <Avatar icon={<UserOutlined />} size="small" />
          <div>
            <div>{record.user?.fullName || 'Ismi yo\'q'}</div>
            <div style={{ fontSize: 12, color: '#7F8C8D' }}>{record.user?.phone}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Chegirma summasi',
      dataIndex: 'discountAmount',
      key: 'discountAmount',
      render: (value: number) => formatCurrency(value || 0),
    },
    {
      title: 'Ishlatilgan vaqt',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleString('uz-UZ'),
    },
  ];

  const columns = [
    {
      title: 'Kod',
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => <strong>{code}</strong>,
    },
    {
      title: 'Turi',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={type === 'PERCENTAGE' ? 'blue' : 'green'}>
          {type === 'PERCENTAGE' ? 'Foiz' : 'Qat\'iy summa'}
        </Tag>
      ),
    },
    {
      title: 'Qiymat',
      key: 'value',
      render: (_: unknown, record: PromoCode) => formatValue(record),
    },
    {
      title: 'Ishlatilgan',
      key: 'usage',
      render: (_: unknown, record: PromoCode) => (
        <Button
          type="link"
          size="small"
          onClick={() => handleViewUsage(record)}
          style={{ padding: 0 }}
        >
          {record.currentUsages}{record.maxUsages ? ` / ${record.maxUsages}` : ''}
        </Button>
      ),
    },
    {
      title: 'Holat',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'default'}>
          {active ? 'Faol' : 'Nofaol'}
        </Tag>
      ),
    },
    {
      title: 'Amal qilish muddati',
      key: 'dates',
      render: (_: unknown, record: PromoCode) => (
        <div style={{ fontSize: 12 }}>
          {record.startsAt && (
            <div>Boshlanish: {new Date(record.startsAt).toLocaleDateString('uz-UZ')}</div>
          )}
          {record.expiresAt && (
            <div>Tugash: {new Date(record.expiresAt).toLocaleDateString('uz-UZ')}</div>
          )}
          {!record.startsAt && !record.expiresAt && '-'}
        </div>
      ),
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_: unknown, record: PromoCode) => (
        <Space>
          <Button
            type="text"
            icon={<HistoryOutlined />}
            onClick={() => handleViewUsage(record)}
            title="Ishlatilish tarixi"
          />
          <Popconfirm
            title={record.isActive ? 'O\'chirishni tasdiqlaysizmi?' : 'Yoqishni tasdiqlaysizmi?'}
            onConfirm={() => handleToggle(record.id)}
          >
            <Button
              type="text"
              icon={record.isActive ? <CloseOutlined /> : <CheckOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <List
        title="Promo kodlar"
        headerButtons={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => create('admin/promo-codes')}
          >
            Yangi promo kod
          </Button>
        }
      >
        <Table {...tableProps} columns={columns} rowKey="id" />
      </List>

      {/* Usage History Modal */}
      <Modal
        title={
          <Space>
            <HistoryOutlined />
            <span>Ishlatilish tarixi: {usageData?.promoCode?.code}</span>
          </Space>
        }
        open={usageModalOpen}
        onCancel={() => {
          setUsageModalOpen(false);
          setUsageData(null);
        }}
        footer={null}
        width={700}
      >
        {usageData && (
          <div>
            <div style={{ marginBottom: 16, padding: 16, background: '#F5F7FA', borderRadius: 8 }}>
              <Space size="large">
                <div>
                  <div style={{ color: '#7F8C8D', fontSize: 12 }}>Turi</div>
                  <Tag color={usageData.promoCode.type === 'PERCENTAGE' ? 'blue' : 'green'}>
                    {usageData.promoCode.type === 'PERCENTAGE' ? 'Foiz' : 'Qat\'iy summa'}
                  </Tag>
                </div>
                <div>
                  <div style={{ color: '#7F8C8D', fontSize: 12 }}>Qiymat</div>
                  <strong>{formatValue(usageData.promoCode)}</strong>
                </div>
                <div>
                  <div style={{ color: '#7F8C8D', fontSize: 12 }}>Jami ishlatilgan</div>
                  <strong>{usageData.total}</strong>
                </div>
              </Space>
            </div>

            {usageData.usages.length > 0 ? (
              <Table
                dataSource={usageData.usages}
                columns={usageColumns}
                rowKey="id"
                pagination={{ pageSize: 10 }}
                size="small"
                loading={loadingUsage}
              />
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#7F8C8D' }}>
                Bu promo kod hali ishlatilmagan
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};
